import { Box, Button, Center, Container, Group, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useListObjects, useSearchObjects } from '@/api/generated/objects/objects';
import {
  CardSection,
  fitColumnWidth,
  ListPagination,
  MobileFab,
  MobileListFilters,
  PageHeading,
  SearchField,
  SelectField,
  StatusBanner,
  StatusPill,
  TABLE_LINK_ACTION_COL_WIDTH,
  TABLE_STATUS_COL_WIDTH,
  tableCenteredCellStyle,
  tableMinWidth,
  tableRowStyle
} from '@/components/common';
import { ObjectFormModal } from '@/features/price-lists/components/ObjectFormModal';
import {
  formatObjectType,
  getObjectTypeConfig,
  objectStatusConfig
} from '@/features/price-lists/objectDisplay';
import { usePageSize } from '@/utils/usePageSize';
import { useUrlPageParam, useUrlTextParam } from '@/utils/useUrlFilters';

export const ObjectRegistryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useUrlPageParam();
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useUrlTextParam('search');
  const [statusFilter, setStatusFilter] = useUrlTextParam('status', 'ALL');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // page dolazi iz URL-a (useUrlPageParam) — na inicijalnom mountu (npr. povratak
  // s detalja preko BackButton-a) NE smije se resetirati na 1, samo kad korisnik
  // stvarno promijeni neki filter nakon toga.
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, statusFilter, pageSize]);

  const { data, isLoading } = useSearchObjects(
    {
      page,
      pageSize,
      search: debouncedSearch.trim() || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter
    },
    { query: { refetchOnMount: 'always' } }
  );
  const pageItems = data?.content ?? [];
  const codeColWidth = useMemo(
    () =>
      fitColumnWidth(
        pageItems.map((o) => o.objectCode ?? ''),
        '600 13.5px',
        50,
        160
      ),
    [pageItems]
  );
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Puna lista (bez paginacije) samo za provjeru zauzetih šifri u
  // ObjectFormModalu — pretraga za prikaz liste ide preko /search iznad.
  const { data: allObjectsData } = useListObjects();

  const paginationLabel = t('objectRegistry.showing', {
    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, totalElements),
    total: totalElements
  });

  const statusOptions = [
    { value: 'ALL', label: t('common.filters.allStatuses') },
    { value: 'ACTIVE', label: t('objectRegistry.statusActive') },
    { value: 'INACTIVE', label: t('objectRegistry.statusInactive') }
  ];

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const goToDetail = (id?: string) => {
    if (id) navigate(`/templates/object-registry/${id}`);
  };

  const columns: [string, number, ('center' | undefined)?][] = [
    ['objectRegistry.colCode', codeColWidth],
    ['objectRegistry.colName', 220],
    ['objectRegistry.colType', 140],
    ['objectRegistry.colLocation', 160],
    ['objectRegistry.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
  ];
  const tableMinWidthPx = tableMinWidth(
    [...columns.map(([, width]) => width), TABLE_LINK_ACTION_COL_WIDTH],
    20
  );

  const detailsLinkStyle = {
    color: 'var(--color-accent-primary-active)',
    textDecoration: 'underline'
  } as const;

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        <Group justify="space-between" wrap="nowrap" visibleFrom="sm">
          <PageHeading title={t('objectRegistry.title')} subtitle={t('objectRegistry.subtitle')} />
          <Button onClick={() => setCreateModalOpened(true)}>
            + {t('objectRegistry.newObject')}
          </Button>
        </Group>

        <Stack gap={12} hiddenFrom="sm">
          <PageHeading title={t('objectRegistry.title')} subtitle={t('objectRegistry.subtitle')} />
          <MobileFab onClick={() => setCreateModalOpened(true)}>
            + {t('objectRegistry.newObject')}
          </MobileFab>
        </Stack>

        <Box hiddenFrom="sm" w="100%">
          <MobileListFilters
            search={
              <SearchField
                hideLabel
                placeholder={t('objectRegistry.searchPlaceholder')}
                value={search}
                onChange={(event) => handleSearchChange(event.currentTarget.value)}
              />
            }
            filters={[
              {
                key: 'status',
                label: t('common.filters.status'),
                options: statusOptions,
                value: statusFilter,
                onChange: (value) => setStatusFilter(value ?? 'ALL'),
                emptyValue: 'ALL',
                variant: 'buttons'
              }
            ]}
          />
        </Box>

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <Box w={{ base: '100%', sm: 280 }}>
            <SearchField
              hideLabel
              placeholder={t('objectRegistry.searchPlaceholder')}
              value={search}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
            />
          </Box>
          <Box w={220}>
            <SelectField
              hideLabel
              data={statusOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value ?? 'ALL')}
              allowDeselect={false}
            />
          </Box>
        </Group>

        <Box visibleFrom="sm">
          <CardSection>
            {!isLoading && pageItems.length === 0 && (
              <Center py={40}>
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('objectRegistry.emptyState')}
                </Text>
              </Center>
            )}

            {pageItems.length > 0 && (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ minWidth: tableMinWidthPx }}>
                    <div
                      className="table-header-row"
                      style={{
                        ...tableRowStyle,
                        padding: '12px 20px',
                        backgroundColor: 'var(--color-bg-canvas)',
                        borderBottom: '1px solid var(--color-border-soft)'
                      }}
                    >
                      {columns.map(([key, width, align]) => (
                        <Text
                          key={key}
                          fz={10}
                          fw={600}
                          w={width}
                          ta={align}
                          style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                        >
                          {t(key)}
                        </Text>
                      ))}
                      <Text
                        fz={10}
                        fw={600}
                        w={TABLE_LINK_ACTION_COL_WIDTH}
                        style={{
                          ...tableCenteredCellStyle,
                          color: 'var(--color-text-muted)',
                          letterSpacing: 0.4
                        }}
                      >
                        {t('objectRegistry.colAction')}
                      </Text>
                    </div>

                    {pageItems.map((obj, index) => {
                      const typeConfig = getObjectTypeConfig(obj.objectType);
                      const statusConfig = obj.isActive
                        ? objectStatusConfig.active
                        : objectStatusConfig.inactive;
                      return (
                        <div
                          key={obj.id}
                          style={{
                            ...tableRowStyle,
                            padding: '14px 20px',
                            borderTop:
                              index === 0 ? undefined : '1px solid var(--color-border-soft)'
                          }}
                        >
                          <Text
                            fz={13.5}
                            fw={600}
                            w={codeColWidth}
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {obj.objectCode}
                          </Text>
                          <Text
                            fz={13}
                            w={220}
                            style={{
                              color: 'var(--color-text-secondary)',
                              fontFamily: 'var(--mantine-font-family-monospace)'
                            }}
                          >
                            {obj.objectName}
                          </Text>
                          <div style={{ width: 140 }}>
                            <StatusPill
                              label={formatObjectType(t, obj.objectType)}
                              bg={typeConfig.bg}
                              fg={typeConfig.fg}
                            />
                          </div>
                          <Text
                            fz={13}
                            w={160}
                            style={{
                              color: 'var(--color-text-muted)',
                              fontFamily: 'var(--mantine-font-family-monospace)'
                            }}
                          >
                            {obj.location || '—'}
                          </Text>
                          <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                            <StatusPill
                              label={t(statusConfig.labelKey)}
                              bg={statusConfig.bg}
                              fg={statusConfig.fg}
                            />
                          </div>
                          <Text
                            fz={12}
                            fw={600}
                            w={TABLE_LINK_ACTION_COL_WIDTH}
                            ta="center"
                            role="button"
                            tabIndex={0}
                            onClick={() => goToDetail(obj.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') goToDetail(obj.id);
                            }}
                            style={{ ...detailsLinkStyle, cursor: 'pointer' }}
                          >
                            {t('objectRegistry.detailsLink')}
                          </Text>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <ListPagination
                  label={paginationLabel}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </CardSection>
        </Box>

        <Stack hiddenFrom="sm" gap={12}>
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('objectRegistry.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.length > 0 && (
            <>
              {pageItems.map((obj) => {
                const typeConfig = getObjectTypeConfig(obj.objectType);
                const statusConfig = obj.isActive
                  ? objectStatusConfig.active
                  : objectStatusConfig.inactive;
                return (
                  <Stack
                    key={obj.id}
                    gap={14}
                    p={16}
                    style={{
                      borderRadius: 12,
                      border: '1px solid var(--color-border-default)',
                      backgroundColor: 'var(--color-bg-surface)'
                    }}
                  >
                    <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                      {obj.objectName}
                    </Text>
                    <Stack gap={6}>
                      <Group justify="space-between" wrap="nowrap">
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {t('objectRegistry.colCode')}
                        </Text>
                        <Text
                          fz={12.5}
                          style={{
                            color: 'var(--color-text-secondary)',
                            fontFamily: 'var(--mantine-font-family-monospace)'
                          }}
                        >
                          {obj.objectCode}
                        </Text>
                      </Group>
                      <Group justify="space-between" wrap="nowrap">
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {t('objectRegistry.colLocation')}
                        </Text>
                        <Text
                          fz={12.5}
                          style={{
                            color: 'var(--color-text-secondary)',
                            fontFamily: 'var(--mantine-font-family-monospace)'
                          }}
                        >
                          {obj.location || '—'}
                        </Text>
                      </Group>
                    </Stack>
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Group gap={8} wrap="wrap">
                        <StatusPill
                          label={formatObjectType(t, obj.objectType)}
                          bg={typeConfig.bg}
                          fg={typeConfig.fg}
                        />
                        <StatusPill
                          label={t(statusConfig.labelKey)}
                          bg={statusConfig.bg}
                          fg={statusConfig.fg}
                        />
                      </Group>
                      <Text
                        fz={12.5}
                        fw={600}
                        role="button"
                        tabIndex={0}
                        onClick={() => goToDetail(obj.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') goToDetail(obj.id);
                        }}
                        style={{ ...detailsLinkStyle, cursor: 'pointer' }}
                      >
                        {t('objectRegistry.detailsLink')}
                      </Text>
                    </Group>
                  </Stack>
                );
              })}

              <ListPagination
                label={paginationLabel}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                bordered={false}
              />
            </>
          )}
        </Stack>
      </Stack>

      <ObjectFormModal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        existingObjectCodes={(allObjectsData ?? [])
          .map((o) => o.objectCode)
          .filter((code): code is string => !!code)}
        onCreated={() => setCreateModalOpened(false)}
        onSaved={setSavedMessage}
      />
    </Container>
  );
};
